import { useState } from 'react'
import {
  mcpScopeDescriptions,
  mcpScopeLabels,
} from '../../mcp-management-model'
import type { McpScope } from '@amitable/shared/mcp-contract'
import type { AuthorizationRequest } from './mcp-authorization-api'
import { Button } from '@/shared/ui/button'

export function McpAuthorizationForm({
  request,
  pending,
  onDecision,
}: {
  request: AuthorizationRequest
  pending: boolean
  onDecision: (
    accept: boolean,
    workspaceId: string,
    scopes: Array<McpScope>,
  ) => void
}) {
  const [workspaceId, setWorkspaceId] = useState(
    request.workspaces[0]?.id ?? '',
  )
  const [scopes, setScopes] = useState<Array<McpScope>>(() =>
    request.permissions.filter((scope) => scope.endsWith('_READ')),
  )
  const canApprove =
    workspaceId && scopes.length > 0 && !request.invalidationPending

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault()
        if (canApprove) onDecision(true, workspaceId, scopes)
      }}
    >
      <AuthorizationDetails
        request={request}
        pending={pending}
        workspaceId={workspaceId}
        onWorkspace={setWorkspaceId}
      />
      <AuthorizationPermissions
        request={request}
        pending={pending}
        scopes={scopes}
        setScopes={setScopes}
      />
      <p className="text-sm text-muted-foreground">
        Read permissions are selected by default. Select each write permission
        explicitly to allow changes. You can revoke this connection in MCP
        settings.
      </p>
      <div className="flex gap-3">
        <Button type="submit" disabled={pending || !canApprove}>
          {pending ? 'Completing authorization...' : 'Approve connection'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => onDecision(false, workspaceId, [])}
        >
          Deny
        </Button>
      </div>
    </form>
  )
}

function AuthorizationDetails({
  request,
  pending,
  workspaceId,
  onWorkspace,
}: {
  request: AuthorizationRequest
  pending: boolean
  workspaceId: string
  onWorkspace: (id: string) => void
}) {
  return (
    <>
      <p>
        Signed in as <strong>{request.account.name}</strong> (
        {request.account.username}).
      </p>
      <div className="space-y-1 rounded border p-3">
        <h2 className="font-semibold">{request.app.name}</h2>
        <p className="text-sm text-muted-foreground">
          Unverified app name supplied by the client.
        </p>
        <p className="break-all text-sm">
          Client identity: {request.app.clientId}
        </p>
        {request.app.metadataUrl ? (
          <p className="break-all text-sm">
            Published metadata: {request.app.metadataUrl}
          </p>
        ) : null}
      </div>
      <label className="block space-y-2">
        <span className="font-medium">Workspace</span>
        <select
          className="w-full rounded border bg-background p-2"
          value={workspaceId}
          onChange={(event) => onWorkspace(event.target.value)}
          disabled={pending || Boolean(request.fixedWorkspaceId)}
        >
          {!request.workspaces.length ? (
            <option value="">No eligible workspace</option>
          ) : null}
          {request.workspaces.map((workspace) => (
            <option key={workspace.id} value={workspace.id}>
              {workspace.name}
            </option>
          ))}
        </select>
      </label>
      <p className="text-sm text-muted-foreground">
        This app stays bound to one workspace for your account. Existing roles
        and feature availability apply.
      </p>
      {!request.workspaces.length ? (
        <p role="alert">
          Your account has no eligible workspace for this connection.
        </p>
      ) : null}
      {request.invalidationPending ? (
        <p role="alert">
          Token invalidation is still pending. Retry revocation in MCP settings
          before connecting again.
        </p>
      ) : null}
    </>
  )
}

function AuthorizationPermissions({
  request,
  pending,
  scopes,
  setScopes,
}: {
  request: AuthorizationRequest
  pending: boolean
  scopes: Array<McpScope>
  setScopes: (change: (current: Array<McpScope>) => Array<McpScope>) => void
}) {
  return (
    <fieldset className="space-y-3" disabled={pending}>
      <legend className="mb-2 font-medium">Requested permissions</legend>
      {request.permissions.map((scope) => (
        <label key={scope} className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={scopes.includes(scope)}
            onChange={(event) =>
              setScopes((current) =>
                event.target.checked
                  ? [...current, scope]
                  : current.filter((value) => value !== scope),
              )
            }
          />
          <span>
            <span className="block font-medium">{mcpScopeLabels[scope]}</span>
            <span className="text-sm text-muted-foreground">
              {mcpScopeDescriptions[scope]}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}
